import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText,
  Download,
  Shield,
  Clock,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronUp,
  User,
  MapPin,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.lifely.guide';

const MAP_LABELS = {
  crypto: 'Crypto & Digital Assets',
  shares: 'Shares & Investments',
  property: 'Property',
  vehicles: 'Vehicles',
  pets: 'Pets & Animals',
  art: 'Art & Collectibles',
  social: 'Social & Digital Accounts',
  physical: 'Physical Documents',
  insurance: 'Insurance',
  'final-wishes': 'Final Wishes',
  liabilities: 'Liabilities & Debts',
  'other-info': 'Other Information',
};

const NomineeAccessPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/export/nominee/${token}`);
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || 'Failed to load data');
      } else {
        setData(json);
      }
    } catch (err) {
      setError('Unable to connect to the server. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownloadPDF = () => {
    if (!data) return;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = 20;
    const today = new Date().toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });

    const checkPageBreak = (h = 30) => {
      if (yPos + h > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        yPos = 20;
      }
    };

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(`Lifely - Information for ${data.userName}`, margin, yPos);
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${today}`, margin, yPos);
    yPos += 6;
    doc.text(`Accessed via secure nominee link`, margin, yPos);
    yPos += 10;

    // Disclaimer
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, yPos, pageWidth - margin * 2, 22, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('IMPORTANT', margin + 4, yPos + 5);
    doc.setFont('helvetica', 'normal');
    doc.text('This document contains sensitive personal information shared via Lifely\'s continuity system.', margin + 4, yPos + 10);
    doc.text('Handle with care and only share with people who have a legitimate need. This is a point-in-time snapshot.', margin + 4, yPos + 15);
    yPos += 28;

    // Will Status
    if (data.willStatus) {
      checkPageBreak(30);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Will & Legal Status', margin, yPos);
      yPos += 8;
      const wsRows = [];
      const ws = data.willStatus;
      if (ws.willExists) wsRows.push(['Will Exists', ws.willExists]);
      if (ws.dateCreated) wsRows.push(['Date Created', ws.dateCreated]);
      if (ws.heldWith) wsRows.push(['Held With', ws.heldWith]);
      if (ws.firmName) wsRows.push(['Firm', ws.firmName]);
      if (ws.location) wsRows.push(['Location', ws.location]);
      if (ws.executor1) wsRows.push(['Executor 1', ws.executor1]);
      if (ws.executor2) wsRows.push(['Executor 2', ws.executor2]);
      if (ws.poaExists) wsRows.push(['POA Exists', ws.poaExists]);
      if (ws.poaType) wsRows.push(['POA Type', ws.poaType]);
      if (ws.legalAdvisor) wsRows.push(['Legal Advisor', ws.legalAdvisor]);
      if (ws.notes) wsRows.push(['Notes', ws.notes]);
      if (wsRows.length) {
        autoTable(doc, {
          startY: yPos,
          head: [['Field', 'Value']],
          body: wsRows,
          margin: { left: margin, right: margin },
          styles: { fontSize: 9 },
        });
        yPos = doc.lastAutoTable.finalY + 10;
      }
    }

    // Contacts
    if (data.contacts?.length) {
      checkPageBreak(30);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Contacts', margin, yPos);
      yPos += 8;
      autoTable(doc, {
        startY: yPos,
        head: [['Name', 'Category', 'Role', 'Email', 'Phone', 'Notes']],
        body: data.contacts.map(c => [c.name, c.category || '', c.role || '', c.email || '', c.phone || '', c.notes || '']),
        margin: { left: margin, right: margin },
        styles: { fontSize: 8 },
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }

    // Maps
    Object.entries(data.maps || {}).forEach(([mapType, mapData]) => {
      if (!mapData.entries?.length) return;
      checkPageBreak(30);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(MAP_LABELS[mapType] || mapType, margin, yPos);
      yPos += 6;
      if (mapData.notes) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.text(`Notes: ${mapData.notes}`, margin, yPos);
        yPos += 5;
      }
      yPos += 2;
      const cols = Object.keys(mapData.entries[0] || {}).filter(k =>
        !['wishesContactName', 'wishesNotes'].includes(k)
      );
      const head = cols.map(k => k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()));
      const body = mapData.entries.map(e => cols.map(k => {
        const v = e[k];
        return v === true ? 'Yes' : v === false ? 'No' : (v ?? '');
      }));
      autoTable(doc, {
        startY: yPos,
        head: [head],
        body,
        margin: { left: margin, right: margin },
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [24, 24, 27] },
      });
      yPos = doc.lastAutoTable.finalY + 10;
    });

    doc.save(`lifely-${data.userName.replace(/\s+/g, '-').toLowerCase()}-export.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-zinc-400 mx-auto" />
          <p className="text-zinc-500">Loading secure export...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="nominee-access-error">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900">Access Unavailable</h1>
            <p className="text-zinc-500">{error}</p>
            <p className="text-sm text-zinc-400">If you believe this is an error, please contact the person who shared this link or email support@lifely.guide.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const mapEntries = Object.entries(data.maps || {});
  const totalEntries = mapEntries.reduce((sum, [, m]) => sum + (m.entries?.length || 0), 0);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="nominee-access-page">
      {/* Header */}
      <header className="bg-zinc-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-6 h-6 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">Secure Nominee Access</span>
          </div>
          <h1 className="text-2xl font-semibold">{data.userName}'s Lifely Information</h1>
          <p className="text-zinc-400 text-sm mt-1">
            Shared via continuity system &middot; {new Date(data.generatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 sm:px-6 space-y-6">
        {/* Warning banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3" data-testid="nominee-access-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Please handle with care</p>
            <p>This document contains sensitive personal information. Only share with people who have a legitimate need to know. This link is available for 7 days from when it was issued - you can revisit and download during this period.</p>
          </div>
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-zinc-500">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{mapEntries.length} categories</span>
            <span className="flex items-center gap-1.5"><FileText className="w-4 h-4" />{totalEntries} entries</span>
            {data.willStatus && <Badge variant="outline">Will Status included</Badge>}
            {data.contacts && <Badge variant="outline">{data.contacts.length} contacts</Badge>}
          </div>
          <Button onClick={handleDownloadPDF} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" />Download PDF
          </Button>
        </div>

        {/* Will Status */}
        {data.willStatus && (
          <WillStatusCard ws={data.willStatus} />
        )}

        {/* Contacts */}
        {data.contacts?.length > 0 && (
          <ContactsCard contacts={data.contacts} />
        )}

        {/* Maps */}
        {mapEntries.map(([mapType, mapData]) => (
          <MapCard key={mapType} mapType={mapType} mapData={mapData} />
        ))}

        {/* Footer */}
        <div className="text-center text-xs text-zinc-400 py-8 border-t">
          <p>Powered by <a href="https://lifely.guide" className="text-zinc-500 hover:underline" target="_blank" rel="noreferrer">Lifely</a> &middot; Privacy-first life mapping</p>
          <p className="mt-1">This link expires on {new Date(data.expiresAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </main>
    </div>
  );
};

/* ─── Sub-components ──────────────────────────────────── */

const WillStatusCard = ({ ws }) => (
  <Card data-testid="nominee-will-status">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <FileText className="w-4 h-4" />Will & Legal Status
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
        {ws.willExists && <Field label="Will Exists" value={ws.willExists} />}
        {ws.dateCreated && <Field label="Date Created" value={ws.dateCreated} />}
        {ws.heldWith && <Field label="Held With" value={ws.heldWith} />}
        {ws.firmName && <Field label="Firm" value={ws.firmName} />}
        {ws.location && <Field label="Location" value={ws.location} />}
        {ws.executor1 && <Field label="Executor 1" value={ws.executor1} />}
        {ws.executor2 && <Field label="Executor 2" value={ws.executor2} />}
        {ws.poaExists && <Field label="POA Exists" value={ws.poaExists} />}
        {ws.poaType && <Field label="POA Type" value={ws.poaType} />}
        {ws.legalAdvisor && <Field label="Legal Advisor" value={ws.legalAdvisor} />}
      </div>
      {ws.notes && (
        <div className="mt-4 pt-3 border-t text-sm">
          <span className="text-zinc-500 font-medium">Notes:</span>
          <p className="text-zinc-700 mt-1">{ws.notes}</p>
        </div>
      )}
    </CardContent>
  </Card>
);

const ContactsCard = ({ contacts }) => (
  <Card data-testid="nominee-contacts">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <User className="w-4 h-4" />Contacts ({contacts.length})
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-zinc-500">
              <th className="pb-2 pr-4 font-medium">Name</th>
              <th className="pb-2 pr-4 font-medium">Category</th>
              <th className="pb-2 pr-4 font-medium">Role</th>
              <th className="pb-2 pr-4 font-medium">Email</th>
              <th className="pb-2 pr-4 font-medium">Phone</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-2 pr-4 font-medium text-zinc-900">{c.name}</td>
                <td className="py-2 pr-4 text-zinc-600 capitalize">{c.category || '-'}</td>
                <td className="py-2 pr-4 text-zinc-600">{c.role || '-'}</td>
                <td className="py-2 pr-4 text-zinc-600">{c.email || '-'}</td>
                <td className="py-2 pr-4 text-zinc-600">{c.phone || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

const MapCard = ({ mapType, mapData }) => {
  const [open, setOpen] = useState(true);
  if (!mapData.entries?.length) return null;

  const label = MAP_LABELS[mapType] || mapType;
  const cols = Object.keys(mapData.entries[0] || {}).filter(k =>
    !['wishesContactName', 'wishesNotes'].includes(k) && mapData.entries.some(e => e[k] !== null && e[k] !== undefined && e[k] !== '')
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card data-testid={`nominee-map-${mapType}`}>
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-3 cursor-pointer hover:bg-zinc-50/50 flex flex-row items-center justify-between">
            <CardTitle className="text-base">{label} ({mapData.entries.length})</CardTitle>
            {open ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent>
            {mapData.notes && (
              <p className="text-sm text-zinc-500 italic mb-3">Notes: {mapData.notes}</p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-zinc-500">
                    {cols.map(k => (
                      <th key={k} className="pb-2 pr-4 font-medium text-xs">
                        {k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mapData.entries.map((entry, i) => (
                    <tr key={i} className="border-b last:border-0">
                      {cols.map(k => (
                        <td key={k} className="py-2 pr-4 text-zinc-700 max-w-[200px] truncate">
                          {entry[k] === true ? 'Yes' : entry[k] === false ? 'No' : (entry[k] ?? '-')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

const Field = ({ label, value }) => (
  <div>
    <dt className="text-zinc-500">{label}</dt>
    <dd className="text-zinc-900 font-medium capitalize">{value}</dd>
  </div>
);

export default NomineeAccessPage;
